import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiCheckSquare, FiFolder, FiUser, FiHome, FiX, FiExternalLink, FiTrash2 } from 'react-icons/fi';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const TYPE_CONFIG = {
  leave:   { label: 'Leave',            icon: '🌴', color: '#22c55e' },
  holiday: { label: 'Holiday',          icon: '🎉', color: '#ef4444' },
  task:    { label: 'Task Due',         icon: '✅', color: 'var(--pm-primary)' },
  project: { label: 'Project Deadline', icon: '📁', color: 'var(--pm-secondary)' },
  wfh:     { label: 'WFH',             icon: '🏠', color: '#06b6d4' },
};

const FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'leave',   label: 'Leaves' },
  { key: 'holiday', label: 'Holidays' },
  { key: 'task',    label: 'Tasks' },
  { key: 'project', label: 'Projects' },
  { key: 'wfh',     label: 'WFH' },
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function buildCalendarGrid(year, month) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const prevMonthDays = getDaysInMonth(year, month - 1 < 0 ? 11 : month - 1);

  const cells = [];

  // Previous month trailing days
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = prevMonthDays - i;
    const pm = month - 1 < 0 ? 11 : month - 1;
    const py = month - 1 < 0 ? year - 1 : year;
    cells.push({ day: d, month: pm, year: py, current: false });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, current: true });
  }

  // Next month leading days
  const remaining = 42 - cells.length;
  const nm = month + 1 > 11 ? 0 : month + 1;
  const ny = month + 1 > 11 ? year + 1 : year;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, month: nm, year: ny, current: false });
  }

  return cells;
}

function padDate(n) {
  return String(n).padStart(2, '0');
}

function cellDateKey(cell) {
  return `${cell.year}-${padDate(cell.month + 1)}-${padDate(cell.day)}`;
}

function todayKey() {
  const t = new Date();
  return `${t.getFullYear()}-${padDate(t.getMonth() + 1)}-${padDate(t.getDate())}`;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      padding: '16px 20px',
      flex: '1 1 0',
      minWidth: 120,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: color + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

// ─── Event Chip ───────────────────────────────────────────────────────────────
function EventChip({ event, onClick }) {
  const cfg = TYPE_CONFIG[event.type] || {};
  const color = event.color || cfg.color || 'var(--pm-primary)';
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(event); }}
      title={event.title}
      style={{
        background: color + '22',
        borderLeft: `3px solid ${color}`,
        color: color,
        borderRadius: 4,
        padding: '1px 5px',
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1.6,
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = color + '40'}
      onMouseLeave={e => e.currentTarget.style.background = color + '22'}
    >
      {cfg.icon} {event.title}
    </div>
  );
}

// ─── Day Cell ─────────────────────────────────────────────────────────────────
function DayCell({ cell, events, isToday, onDayClick, onEventClick }) {
  const [hovered, setHovered] = React.useState(false);
  const visible = events.slice(0, 3);
  const extra = events.length - 3;

  return (
    <div
      onClick={() => events.length > 0 && onDayClick(cell, events)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        minHeight: 100,
        padding: '6px 6px 4px',
        borderRight: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0',
        background: isToday
          ? '#eef2ff'
          : hovered && events.length > 0
          ? '#f8fafc'
          : '#fff',
        cursor: events.length > 0 ? 'pointer' : 'default',
        transition: 'background 0.15s',
        position: 'relative',
        outline: isToday ? '2px solid #6366f1' : 'none',
        outlineOffset: -2,
        boxSizing: 'border-box',
      }}
    >
      {/* Day number */}
      <div style={{
        fontSize: 13,
        fontWeight: isToday ? 700 : 500,
        color: isToday ? 'var(--pm-primary)' : cell.current ? '#1e293b' : '#cbd5e1',
        marginBottom: 4,
        width: 22, height: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '50%',
        background: isToday ? 'var(--pm-primary)' : 'transparent',
        color: isToday ? '#fff' : cell.current ? '#1e293b' : '#cbd5e1',
      }}>
        {cell.day}
      </div>

      {/* Event chips */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visible.map((ev) => (
          <EventChip key={ev.id} event={ev} onClick={onEventClick} />
        ))}
        {extra > 0 && (
          <div style={{
            fontSize: 10, color: 'var(--pm-primary)', fontWeight: 600,
            padding: '1px 4px', cursor: 'pointer',
          }}>
            +{extra} more
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Day Events Modal ─────────────────────────────────────────────────────────
function DayModal({ cell, events, onClose, onDelete, onNavigate }) {
  if (!cell) return null;

  const dateLabel = new Date(cell.year, cell.month, cell.day).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.4)',
        backdropFilter: 'blur(2px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%', maxWidth: 480,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '18px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{dateLabel}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
              {events.length} event{events.length !== 1 ? 's' : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none', background: '#f1f5f9',
              borderRadius: 8, width: 32, height: 32,
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: '#64748b', fontSize: 16,
            }}
          >
            <FiX />
          </button>
        </div>

        {/* Event list */}
        <div style={{ overflowY: 'auto', padding: '12px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {events.map(ev => {
            const cfg = TYPE_CONFIG[ev.type] || {};
            const color = ev.color || cfg.color || 'var(--pm-primary)';
            return (
              <div key={ev.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 14px',
                background: color + '0d',
                border: `1px solid ${color}30`,
                borderRadius: 10,
              }}>
                {/* Color bar */}
                <div style={{ width: 4, borderRadius: 4, background: color, alignSelf: 'stretch', flexShrink: 0 }} />

                {/* Icon */}
                <div style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>{cfg.icon}</div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14, marginBottom: 3 }}>
                    {ev.title}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '0.5px', color, padding: '2px 7px',
                      background: color + '22', borderRadius: 20,
                    }}>
                      {cfg.label}
                    </span>
                    {ev.status && (
                      <span style={{
                        fontSize: 11, color: '#64748b',
                        background: '#f1f5f9', borderRadius: 20,
                        padding: '2px 7px', textTransform: 'capitalize',
                      }}>
                        {ev.status}
                      </span>
                    )}
                    {ev.projectName && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>
                        📂 {ev.projectName}
                      </span>
                    )}
                    {ev.user && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>
                        👤 {ev.user.name}
                      </span>
                    )}
                  </div>
                  {ev.endDate && ev.endDate !== ev.date && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      Until {new Date(ev.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  )}
                  {/* Action buttons */}
                  <div style={{ display:'flex', gap:6, marginTop:8 }}>
                    {(ev.type === 'task') && (
                      <button onClick={() => { onNavigate(`/tasks/${ev.id}`); onClose(); }}
                        style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        <FiExternalLink size={11}/> View Task
                      </button>
                    )}
                    {(ev.type === 'project') && (
                      <button onClick={() => { onNavigate(`/projects/${ev.id}`); onClose(); }}
                        style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        <FiExternalLink size={11}/> View Project
                      </button>
                    )}
                    {(ev.type === 'leave' || ev.type === 'wfh' || ev.type === 'holiday') && (
                      <button onClick={() => { onNavigate(ev.type === 'leave' ? '/leaves' : ev.type === 'wfh' ? '/wfh-requests' : '/holidays'); onClose(); }}
                        style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        <FiExternalLink size={11}/> View {ev.type === 'leave' ? 'Leave' : ev.type === 'wfh' ? 'WFH' : 'Holiday'}
                      </button>
                    )}
                    {(ev.type === 'leave' || ev.type === 'wfh' || ev.type === 'holiday') && (
                      <button onClick={() => onDelete(ev)}
                        style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:8, border:'1.5px solid #fee2e2', background:'#fff5f5', color:'#ef4444', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        <FiTrash2 size={11}/> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 14 }}>
      <div style={{
        width: 40, height: 40,
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <div style={{ color: '#64748b', fontSize: 14 }}>Loading events…</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Calendar() {
  const today = new Date();
  const navigate = useNavigate();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [modalCell, setModalCell] = useState(null);
  const [modalEvents, setModalEvents] = useState([]);

  const fetchEvents = useCallback(async (year, month) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/calendar/events', {
        params: { month: month + 1, year },
      });
      setEvents(res.data.data || []);
      setMeta(res.data.meta || null);
    } catch (err) {
      console.error('Calendar fetch error:', err);
      setError('Failed to load calendar events. Please try again.');
      setEvents([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(currentYear, currentMonth);
  }, [currentYear, currentMonth, fetchEvents]);

  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  // Build event map: dateKey -> [events]
  const eventMap = React.useMemo(() => {
    const map = {};
    const filtered = activeFilter === 'all' ? events : events.filter(e => e.type === activeFilter);
    filtered.forEach(ev => {
      // For multi-day events (leave), mark each day in range
      if (ev.endDate && ev.endDate !== ev.date) {
        const start = new Date(ev.date);
        const end = new Date(ev.endDate);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const key = `${d.getFullYear()}-${padDate(d.getMonth() + 1)}-${padDate(d.getDate())}`;
          if (!map[key]) map[key] = [];
          // Avoid duplicates
          if (!map[key].find(x => x.id === ev.id)) map[key].push(ev);
        }
      } else {
        const key = ev.date ? ev.date.slice(0, 10) : null;
        if (key) {
          if (!map[key]) map[key] = [];
          map[key].push(ev);
        }
      }
    });
    return map;
  }, [events, activeFilter]);

  const calendarCells = React.useMemo(() => buildCalendarGrid(currentYear, currentMonth), [currentYear, currentMonth]);
  const todayStr = todayKey();

  const counts = meta?.counts || {};
  const totalEvents = events.length;

  const handleDayClick = (cell, dayEvents) => {
    setModalCell(cell);
    setModalEvents(dayEvents);
  };

  const handleEventClick = (ev) => {
    // Find which cell this event belongs to
    const dateKey = ev.date ? ev.date.slice(0, 10) : null;
    if (dateKey) {
      const [y, m, d] = dateKey.split('-').map(Number);
      setModalCell({ year: y, month: m - 1, day: d, current: true });
      setModalEvents(eventMap[dateKey] || [ev]);
    }
  };

  const closeModal = () => { setModalCell(null); setModalEvents([]); };

  const handleDeleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      const endpointMap = { leave: 'leaves', wfh: 'wfh', holiday: 'holidays' };
      const endpoint = endpointMap[ev.type];
      if (!endpoint) return;
      await api.delete(`/${endpoint}/${ev.id}`);
      toast.success('Deleted successfully');
      await fetchEvents(currentYear, currentMonth);
      setModalEvents(prev => prev.filter(e => e.id !== ev.id));
      if (modalEvents.length <= 1) closeModal();
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      background: '#f8fafc',
      minHeight: '100vh',
      padding: '28px 32px',
      boxSizing: 'border-box',
    }}>
      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px' }}>
            Calendar
          </h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            View leaves, holidays, tasks, project deadlines, and WFH requests
          </p>
        </div>
        {/* Month Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={goToToday}
            style={{
              padding: '8px 16px', border: '1px solid #e2e8f0',
              borderRadius: 8, background: '#fff', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, color: '#475569',
              fontFamily: 'inherit',
            }}
          >
            Today
          </button>
          <button
            onClick={goToPrevMonth}
            style={{
              width: 34, height: 34, border: '1px solid #e2e8f0',
              borderRadius: 8, background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#475569',
            }}
          >
            <FiChevronLeft size={16} />
          </button>
          <div style={{
            minWidth: 140, textAlign: 'center',
            fontSize: 16, fontWeight: 700, color: '#1e293b',
          }}>
            {MONTHS[currentMonth]} {currentYear}
          </div>
          <button
            onClick={goToNextMonth}
            style={{
              width: 34, height: 34, border: '1px solid #e2e8f0',
              borderRadius: 8, background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#475569',
            }}
          >
            <FiChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total Events"      value={totalEvents}         color="var(--pm-primary)" icon="📅" />
        <StatCard label="Leaves"            value={counts.leaves  || 0} color="#22c55e" icon="🌴" />
        <StatCard label="Holidays"          value={counts.holidays|| 0} color="#ef4444" icon="🎉" />
        <StatCard label="Tasks Due"         value={counts.tasks   || 0} color="var(--pm-primary)" icon="✅" />
        <StatCard label="Project Deadlines" value={counts.projects|| 0} color="var(--pm-secondary)" icon="📁" />
      </div>

      {/* ── Legend + Filters ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 16,
      }}>
        {/* Legend */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{cfg.label}</span>
            </div>
          ))}
        </div>

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                border: activeFilter === f.key ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0',
                background: activeFilter === f.key ? 'var(--pm-primary)' : '#fff',
                color: activeFilter === f.key ? '#fff' : '#475569',
                fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
                fontFamily: 'inherit',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Calendar Grid ── */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '2px solid #e2e8f0' }}>
          {DAYS.map(day => (
            <div key={day} style={{
              padding: '10px 8px',
              textAlign: 'center',
              fontSize: 12, fontWeight: 700,
              color: day === 'Sun' ? '#ef4444' : day === 'Sat' ? 'var(--pm-primary)' : '#64748b',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              borderRight: day !== 'Sat' ? '1px solid #f1f5f9' : 'none',
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Loading / Error / Empty states */}
        {loading ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <div style={{ color: '#ef4444', fontWeight: 600, marginBottom: 8 }}>{error}</div>
            <button
              onClick={() => fetchEvents(currentYear, currentMonth)}
              style={{
                padding: '8px 18px', background: 'var(--pm-primary)', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {calendarCells.map((cell, idx) => {
              const key = cellDateKey(cell);
              const dayEvents = eventMap[key] || [];
              const isToday = key === todayStr && cell.current;
              return (
                <DayCell
                  key={idx}
                  cell={cell}
                  events={dayEvents}
                  isToday={isToday}
                  onDayClick={handleDayClick}
                  onEventClick={handleEventClick}
                />
              );
            })}
          </div>
        )}

        {/* Empty state overlay */}
        {!loading && !error && events.length === 0 && (
          <div style={{
            position: 'absolute',
            left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}>
          </div>
        )}
        {!loading && !error && events.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0 32px', borderTop: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
            <div style={{ fontWeight: 600, color: '#475569', fontSize: 15 }}>No events this month</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
              Events will appear here when scheduled.
            </div>
          </div>
        )}
      </div>

      {/* ── Day Events Modal ── */}
      <DayModal cell={modalCell} events={modalEvents} onClose={closeModal} onDelete={handleDeleteEvent} onNavigate={navigate} />
    </div>
  );
}
